import { loadStripeTerminal } from "@stripe/terminal-js";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { authHeader, clearToken, isUnauthorized } from "../utils/staffAuth";
import { pricingRefusal } from "../utils/serverPricing";

const pathURL = process.env.REACT_APP_API_URL;
// The reader is registered to FXRY's "FXRY Studio" Terminal location in
// Stripe (see claude/fxry-tap-to-pay-integration-spec.md). It connects to
// Stripe over the internet — not tied to any one staff computer — so any
// machine running this checkout with the right location ID can discover and
// use it.
const TERMINAL_LOCATION_ID =
  process.env.REACT_APP_STRIPE_TERMINAL_LOCATION_ID || "tml_Gp0bwwHQWlsS5n";

// The phases where the customer can still change their mind: the reader is
// connecting, or standing there asking for a card. Once processPayment has the
// card, Stripe decides, and the way back is a refund.
const CANCELLABLE = new Set(["connecting", "ready", "collecting"]);

/**
 * Staff-facing tap-to-pay flow. Mirrors CheckoutForm.jsx's job (collect
 * payment, hand back a settled PaymentIntent) but drives Stripe's Terminal
 * SDK against the physical reader instead of Stripe Elements' card form.
 *
 * Order creation happens the same way it does for the discount/complimentary
 * path in Checkout.jsx: only AFTER the reader confirms a successful charge.
 * A decline or reader error surfaces here and creates no order — same
 * behavior as a declined manually-keyed card today.
 *
 * ## Changing their mind at the reader
 *
 * Up to the moment the card is read, Cancel stops the reader and cancels the
 * payment at Stripe (POST order/terminal/cancel-payment-intent), then hands
 * the screen back to the checkout form with the cart intact. Once the card has
 * been read the button is gone, because from there the money is either taken
 * or declined by Stripe, and a refund is the only honest way back.
 */
const TerminalCheckoutForm = ({
  orderData,
  amount,
  name,
  email,
  taxCalculationId,
  giftSaleCertificateId = null,
  onSuccess,
  onCancel,
  onGiftRefused,
  onPricingRefused,
}) => {
  // connecting | ready | collecting | processing | creating_order |
  // cancelling | error
  const [status, setStatus] = useState("connecting");
  const [message, setMessage] = useState("Connecting to reader...");
  const terminalRef = useRef(null);
  const startedRef = useRef(false);
  // The phase as it is right now. `status` in a click handler is the value
  // that render captured, and Cancel has to know whether the card has reached
  // processPayment yet — the one difference between "nothing was charged" and
  // "that is a refund now".
  const phaseRef = useRef("connecting");
  // Set when staff cancel, so the payment still running in the effect below
  // stops quietly instead of reporting a decline nobody caused.
  const stoppedRef = useRef(false);
  // The payment to cancel, once it exists.
  const intentIdRef = useRef(null);

  const enter = (next) => {
    phaseRef.current = next;
    setStatus(next);
  };

  /**
   * Stop the reader and cancel the payment. Only while the reader is still
   * waiting: `CANCELLABLE` is the list, and the server refuses anything later
   * anyway, so a tap that beats this by a moment cannot be reported as
   * cancelled.
   */
  const cancelPayment = async () => {
    if (stoppedRef.current) return;
    if (!CANCELLABLE.has(phaseRef.current)) {
      setMessage(
        "The card is being read now. Wait for it to finish, then refund it if they do not want it.",
      );
      return;
    }
    stoppedRef.current = true;
    enter("cancelling");
    setMessage("Stopping the payment. Nothing has been charged.");

    try {
      await terminalRef.current?.cancelCollectPaymentMethod();
    } catch (readerError) {
      // The reader had already finished asking, or was never asking. Either
      // way the payment below is what decides whether money can still move.
      console.error("[TERMINAL CHECKOUT] stopping the reader:", readerError);
    }

    const paymentIntentId = intentIdRef.current;
    if (paymentIntentId) {
      try {
        await axios.post(
          `${pathURL}order/terminal/cancel-payment-intent`,
          { paymentIntentId },
          { headers: authHeader() },
        );
      } catch (cancelError) {
        console.error(
          "[TERMINAL CHECKOUT] could not cancel",
          paymentIntentId,
          cancelError,
        );
        enter("error");
        setMessage(
          cancelError?.response?.data?.message ||
            `Couldn't cancel payment ${paymentIntentId}. Check it in Stripe before charging anything again.`,
        );
        return;
      }
    }

    if (typeof onCancel === "function") onCancel();
  };

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;
    // Unmounted, or staff pressed Cancel. Either way this run says nothing
    // more on screen: the cancel path owns the message from then on.
    const halted = () => cancelled || stoppedRef.current;
    // Set when the backend refuses this device's login while fetching the
    // connection token. Stripe's SDK swallows that 401 and reports its own
    // "`onFetchConnectionToken` failure" instead, so the catch below never
    // sees the status. This remembers it so staff are told to sign in again.
    let signedOut = false;

    const showSignedOut = () => {
      clearToken();
      enter("error");
      setMessage(
        "This device has been signed out. Refresh the page, sign in again, and take the payment. Nothing was charged.",
      );
    };

    const run = async () => {
      try {
        const StripeTerminal = await loadStripeTerminal();
        const terminal = StripeTerminal.create({
          onFetchConnectionToken: async () => {
            // Both Terminal calls carry the device's staff login
            // (utils/staffAuth.js). Until this app had one they were the only
            // unauthenticated routes in the backend that could reach Stripe.
            try {
              const resp = await axios.post(
                `${pathURL}order/terminal/connection-token`,
                null,
                { headers: authHeader() },
              );
              return resp.data.secret;
            } catch (tokenError) {
              if (isUnauthorized(tokenError)) signedOut = true;
              throw tokenError;
            }
          },
          onUnexpectedReaderDisconnect: () => {
            if (halted()) return;
            enter("error");
            setMessage(
              "The reader disconnected unexpectedly. Check its Wi-Fi connection and try again.",
            );
          },
        });
        terminalRef.current = terminal;

        setMessage("Looking for the reader...");
        const discoverResult = await terminal.discoverReaders({
          location: TERMINAL_LOCATION_ID,
        });
        if (halted()) return;
        if (signedOut) return showSignedOut();

        if (discoverResult.error || !discoverResult.discoveredReaders?.length) {
          enter("error");
          setMessage(
            "Couldn't find the reader. Make sure it's powered on and connected to Wi-Fi.",
          );
          return;
        }

        const reader = discoverResult.discoveredReaders[0];
        const connectResult = await terminal.connectReader(reader);
        if (halted()) return;
        if (signedOut) return showSignedOut();

        if (connectResult.error) {
          enter("error");
          setMessage(
            connectResult.error.message || "Couldn't connect to the reader.",
          );
          return;
        }

        enter("ready");
        setMessage("Creating payment...");

        // Address and cart travel with the intent request: the server refuses
        // a return address the carrier won't take HERE, before the reader
        // charges anything, and parks the cart so the order survives this
        // screen being closed after the tap.
        const piResponse = await axios.post(
          `${pathURL}order/terminal/create-payment-intent`,
          {
            amount,
            name,
            email,
            taxCalculationId,
            source: "fxrypopup-terminal",
            address: orderData?.[0]?.user?.address,
            inbound_delivery_method: orderData?.[0]?.inbound_delivery_method,
            return_delivery_method: orderData?.[0]?.return_delivery_method,
            order_payload: orderData?.[0],
            // A gift card bought on the same bill. The server prices it from
            // the certificate it already holds, and only then will it let the
            // charge be bigger than the repair's tax calculation.
            gift_sale_certificate_id: giftSaleCertificateId || undefined,
          },
          { headers: authHeader() },
        );
        const { paymentIntentId, clientSecret } = piResponse.data;
        intentIdRef.current = paymentIntentId;

        if (halted()) {
          // Cancelled, or the screen closed, while this payment was being
          // made. Cancel it now so nothing is left open at Stripe. Nothing was
          // charged: the reader has not been asked for a card yet.
          await axios
            .post(
              `${pathURL}order/terminal/cancel-payment-intent`,
              { paymentIntentId },
              { headers: authHeader() },
            )
            .catch((cancelError) =>
              console.error(
                "[TERMINAL CHECKOUT] cancelling the new payment:",
                cancelError,
              ),
            );
          return;
        }
        enter("collecting");
        setMessage("Tap, insert, or swipe the card on the reader now.");

        const collectResult = await terminal.collectPaymentMethod(clientSecret);
        if (halted()) return;

        if (collectResult.error) {
          enter("error");
          setMessage(
            collectResult.error.message ||
              "Card was declined or the collection was cancelled.",
          );
          return;
        }

        enter("processing");
        setMessage("Processing payment...");

        const processResult = await terminal.processPayment(
          collectResult.paymentIntent,
        );
        if (halted()) return;

        if (processResult.error) {
          enter("error");
          setMessage(
            processResult.error.message || "Payment could not be processed.",
          );
          return;
        }

        const paymentIntent = processResult.paymentIntent;
        if (paymentIntent.status !== "succeeded") {
          enter("error");
          setMessage(
            "Payment did not complete successfully. No order was created.",
          );
          return;
        }

        // Payment succeeded on the reader — now, and only now, create the
        // order, same order as the existing card flow's Thankyou.jsx logic.
        enter("creating_order");
        setMessage("Payment approved — creating order...");

        // The card has been charged. From here, any failure must say so.
        //
        // axios throws on every 4xx/5xx, so the old `status !== 200` check
        // below this could never run: a refused order fell through to the
        // outer catch, which showed staff only the server's reason ("This
        // address appears to be an apartment...") with no word that the
        // card had gone through. Read that at a busy counter and the natural
        // next move is to charge the card again.
        let orderId;
        try {
          const orderResponse = await axios.post(
            `${pathURL}order/createforpaymentintent`,
            { ...orderData?.[0], payment_intent_id: paymentIntentId },
            { headers: authHeader() },
          );
          orderId = orderResponse?.data?.data?._id;
          if (!orderId) throw new Error("no order id returned");
          await axios.post(`${pathURL}order/callbackpaymentintent/${orderId}`, {
            payment_intent: paymentIntentId,
            payment: "paid",
            payment_method: "terminal",
          });
        } catch (orderError) {
          console.error(
            "[TERMINAL CHECKOUT] charged but order not saved:",
            paymentIntentId,
            orderError,
          );
          if (halted()) return;
          enter("error");
          setMessage(
            `THE CARD WAS CHARGED. Do not charge it again. The order did not save${
              orderError?.response?.data?.error
                ? ` (${orderError.response.data.error})`
                : ""
            }. Write down payment ${paymentIntentId} and send it to hello@thefxry.com so the order can be added.`,
          );
          return;
        }

        // The gift card bought on the same bill goes live now, off the same
        // payment. The server checks that payment really covered it as well
        // as the order. The money is already taken either way, so a failure
        // here says so and names the card, rather than looking like a
        // payment problem.
        if (giftSaleCertificateId) {
          try {
            setMessage("Payment approved — activating the gift card...");
            await axios.post(
              `${pathURL}gift/${giftSaleCertificateId}/terminal-settle`,
              { paymentIntentId },
              { headers: authHeader() },
            );
          } catch (giftError) {
            console.error(
              "[TERMINAL CHECKOUT] paid but gift card not activated:",
              paymentIntentId,
              giftError,
            );
            if (halted()) return;
            enter("error");
            setMessage(
              `The order is saved and the card was charged. The GIFT CARD is not active yet${
                giftError?.response?.data?.message
                  ? ` (${giftError.response.data.message})`
                  : ""
              }. Do not charge anything again. Finish it on the Gift cards page, or send payment ${paymentIntentId} to hello@thefxry.com.`,
            );
            return;
          }
        }

        if (halted()) return;
        onSuccess(orderId);
      } catch (err) {
        if (halted()) return;
        console.error("[TERMINAL CHECKOUT] Error:", err);
        enter("error");
        // A rejected login is not a reader fault, and telling staff to check
        // the reader's Wi-Fi when the real answer is "sign in again" is how
        // somebody ends up power-cycling a working reader at a market. The
        // token is dropped so the app asks for the login on the next load.
        if (signedOut || isUnauthorized(err)) {
          showSignedOut();
          return;
        }
        // The server's price differs, or an override needs a reason
        // (SERVER_PRICING=on). Nothing was charged: Checkout shows the
        // server's figures and staff press Pay again.
        const pricing = pricingRefusal(err);
        if (pricing && typeof onPricingRefused === "function") {
          onPricingRefused(pricing);
          return;
        }
        if (
          err?.response?.status === 400 &&
          err?.response?.data?.field === "address"
        ) {
          setMessage(
            `The carrier won't accept that return address: ${err.response.data.error} Nothing was charged. Fix the address and try again.`,
          );
          return;
        }
        // Refused before the reader took anything, with a gift certificate
        // on the order: most likely it was spent elsewhere since it was
        // checked. Checkout decides and goes back to the form.
        if (
          err?.response?.status === 400 &&
          err?.response?.data?.message &&
          orderData?.[0]?.gift_certificate_code &&
          typeof onGiftRefused === "function"
        ) {
          onGiftRefused(err.response.data.message);
          return;
        }
        setMessage(
          err?.response?.data?.error ||
            err?.message ||
            "Something went wrong talking to the reader. No order was created.",
        );
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="terminal-payment-status" role="status">
      <p>{message}</p>
      {CANCELLABLE.has(status) && (
        <button type="button" className="text-black" onClick={cancelPayment}>
          Cancel payment
        </button>
      )}
      {status === "error" && (
        <button
          type="button"
          className="section-main-btn"
          onClick={() => window.location.reload()}
        >
          Start Over
        </button>
      )}
    </div>
  );
};

export default TerminalCheckoutForm;
